import { CommonModule, DOCUMENT } from '@angular/common';
import { Component, Inject, OnDestroy, OnInit, QueryList, ViewChildren } from '@angular/core';
import { AbstractControl, FormBuilder, FormControl, FormGroup, ReactiveFormsModule, ValidationErrors, ValidatorFn, Validators } from '@angular/forms';
import { HttpClient } from '@angular/common/http';
import { Router } from '@angular/router';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { combineLatest, Subject } from 'rxjs';
import { debounceTime, distinctUntilChanged, filter, finalize, startWith, switchMap, takeUntil, tap } from 'rxjs/operators';
import { ApiService } from '../../../core/services/api.service';
import { GuestTokenService } from '../../../core/services/guest-token.service';
import { environment } from '../../../../environments/environment';
import { SHARED_COMPONENTS } from '../../../shared/shared-imports';
import { SkeletonLoaderComponent } from '../../../shared/components/skeleton-loader/skeleton-loader.component';
import { RoutesPath } from '../../../shared/constants/routes-path';
import { ApiEndPoints, ApiRoot } from '../../../shared/constants/api-endpoints';
import { LookupKey } from '../../../shared/constants/lookup-keys';
import { SharedService } from '../../../shared/services/shared.service';
import { DocumentServiceType, FieldValue, ServiceName, SrActionType } from '../../../shared/constants/services.enum';
import { FileComponent } from '../../../shared/components/controls/file/file.component';
import { AllowedDocumentType, FolderResponse } from '../../../shared/models/documents-response.model';
import { NewUserRequest } from '../../../shared/models/services-request/new-user.model';
import { Utils } from '../../../Utils/utils';
import { customValidator } from '../../../Utils/validator';
import { MembershipDisclaimerComponent } from '../../../shared/components/membership-disclaimer/membership-disclaimer.component';

type UaePassInitResponse = {
  flowId: string;
  redirectUrl: string;
};

@Component({
  standalone: true,
  selector: 'app-register',
  imports: [
    CommonModule,
    ReactiveFormsModule,
    TranslateModule,
    SkeletonLoaderComponent,
    MembershipDisclaimerComponent,
    ...SHARED_COMPONENTS
  ],
  templateUrl: './register.component.html',
  styleUrl: './register.component.css'
})
export class RegisterComponent implements OnInit, OnDestroy {
  @ViewChildren('fileCmp') fileComponents!: QueryList<FileComponent>;

  private destroy$ = new Subject<void>();
  private passwordMatchValidator: ValidatorFn = (group: AbstractControl): ValidationErrors | null => {
    const password = group.get('password')?.value;
    const confirm = group.get('confirmPassword');
    if (!confirm) {
      return null;
    }

    if (!password || !confirm.value) {
      if (confirm.errors?.['password_mismatch']) {
        const { password_mismatch, ...rest } = confirm.errors;
        confirm.setErrors(Object.keys(rest).length ? rest : null);
      }
      return null;
    }

    if (password !== confirm.value) {
      confirm.setErrors({ ...(confirm.errors || {}), password_mismatch: true });
      return { password_mismatch: true };
    }

    if (confirm.errors?.['password_mismatch']) {
      const { password_mismatch, ...rest } = confirm.errors;
      confirm.setErrors(Object.keys(rest).length ? rest : null);
    }

    return null;
  };
  private passwordRulesValidator: ValidatorFn = (control: AbstractControl): ValidationErrors | null => {
    const value = control.value as string;
    if (!value) {
      return null;
    }
    const pattern = /^(?=.*[A-Z])(?=.*\d)(?=.*[^A-Za-z0-9\s])(?!.*\s).{8,}$/;
    return pattern.test(value) ? null : { password_rules: true };
  };
  private arabicLettersOnlyValidator: ValidatorFn = (control: AbstractControl): ValidationErrors | null => {
    const value = control.value as string;
    if (!value) {
      return null;
    }
    const trimmed = value.toString().trim();
    if (!trimmed) {
      return { whitespace: true };
    }
    const pattern = /^[\u0600-\u06FF\s]+$/;
    return pattern.test(trimmed) ? null : { arabic: true };
  };

  form: FormGroup;
  roleTouched = false;
  loadingEmirateInfo = false;
  detailNotFound = false;
  detailFound = false;
  registerError = '';
  members: any[] = [];
  loadingMember = false;
  flags = {
    chatEnabled: environment.mVP1Settings?.chatEnabled ?? true
  };
  isSubmitting = false;
  isSendingVerifyOtp = false;
  verifyEmailModal = false;
  showMembershipDisclaimerModal = false;
  userLoginName = '';
  userEmail = '';
  private pendingRegistrationData: {
    formValue: any;
    userName: string;
    password: string;
  } | null = null;
  private readonly loginUrl = `${environment.wso2APIURL}DCAuthToken/AuthTokenREST/authToken`;
  private readonly uaePassInitUrl = `${environment.wso2APIURL}DCAuthToken/InitiateUAEPassAuthREST/initiateUAEPassAuth`;
  private search$ = new Subject<any>();
  LookupKey = LookupKey;
  documentServiceType = DocumentServiceType;
  srFolder!: FolderResponse;
  mainFromSectionDocs: AllowedDocumentType[] = [];
  loadingDocumentFolder = false;
  lang = 'en';
  private readonly businessDocumentNames = [
    'trade license',
    'emirates id of partners managers and owners',
    'moa share certificate'
  ];
  private readonly residentPersonalDocumentName = 'emirates id';
  private readonly nonResidentPersonalDocumentName = 'passport copy';
  residencyOptions = [
    {
      title: 'REGISTER.RESIDENCY_RESIDENT_TITLE',
      description: 'REGISTER.RESIDENCY_RESIDENT_DESC',
      value: 'resident'
    },
    {
      title: 'REGISTER.RESIDENCY_NON_RESIDENT_TITLE',
      description: 'REGISTER.RESIDENCY_NON_RESIDENT_DESC',
      value: 'non-resident'
    },
  ];
  benefitOptions = [
    {
      title: 'REGISTER.BENEFIT_BUSINESS_TITLE',
      description: 'REGISTER.BENEFIT_BUSINESS_DESC',
      value: 'business'
    },
    {
      title: 'REGISTER.BENEFIT_PERSONAL_TITLE',
      description: 'REGISTER.BENEFIT_PERSONAL_DESC',
      value: 'personal'
    }
  ];
  businessLocationOptions = [
    { label: 'REGISTER.BUSINESS_DUBAI', value: 'dubai' },
    { label: 'REGISTER.BUSINESS_NON_DUBAI', value: 'non-dubai' }
  ];
  roleOptions = [
    {
      label: 'OWNER_FORM.ROLE_OWNER',
      description: 'REGISTER.ROLE_OWNER_DESC',
      value: 'owner'
    },
    {
      label: 'OWNER_FORM.ROLE_MANAGER',
      description: 'REGISTER.ROLE_MANAGER_DESC',
      value: 'manager'
    },
    {
      label: 'REGISTER.ROLE_REP_TITLE',
      description: 'REGISTER.ROLE_REP_DESC',
      value: 'representative'
    }
  ];
  companySearchByOptions = [
    { value: 'CSN', label: 'SEARCH_BY_MEM_NUMBER' },
    { value: 'LicenseNumber', label: 'SEARCH_BY_MEM_LICENSE' }
  ];
  private previousRoles: string[] = [];
  private lastDraftIdentityKey = '';
  srNumber: string = "";
  uaePassFocused = false;
  uaePassPressed = false;
  uaePassSubmitting = false;

  // This method is used to initialize the class instance using fb and router
  constructor(
    private fb: FormBuilder,
    private router: Router,
    private api: ApiService,
    private guestTokenService: GuestTokenService,
    private sharedService: SharedService,
    private http: HttpClient,
    private translate: TranslateService,
    @Inject(DOCUMENT) private document: Document
  ) {
    this.form = this.fb.group({
      residencyStatus: [null, Validators.required],
      benefit: [null, Validators.required],
      businessLocation: [null],
      companySearch: [''],
      emiratesId: [null, [customValidator('emiratesId'), Validators.maxLength(20)]],
      emiratesDob: this.fb.group({
        day: [{ value: null, disabled: true }, [Validators.required]],
        month: [{ value: null, disabled: true }, [Validators.required]],
        year: [{ value: null, disabled: true }, [Validators.required]],
      }),
      passportNumber: [null, [customValidator('safeText'), Validators.maxLength(40)]],
      passportExpiry: this.fb.group({
        day: [null],
        month: [null],
        year: [null],
      }),
      nameEn: [null, [Validators.required, customValidator('english'), Validators.maxLength(150)]],
      nameAr: new FormControl(null, {
        validators: [
          Validators.required,
          customValidator('arabic'),
          this.arabicLettersOnlyValidator,
          Validators.maxLength(150)
        ],
        updateOn: 'blur'
      }),
      nationality: [null, Validators.required],
      roles: this.fb.control([]),
      email: ['', [Validators.required, Validators.email]],
      phone: ['', [Validators.required, customValidator('mobile')]],
      countryCode: [null, Validators.required],
      username: ['', Validators.required],
      password: ['', [Validators.required, this.passwordRulesValidator]],
      confirmPassword: ['', Validators.required],
      termsAccepted: [false, Validators.required],
      issuingAuthority: [null],
    }, { validators: [this.passwordMatchValidator] });
  }

  get isPlatinumTheme(): boolean {
    return !!this.document?.body?.classList.contains('platinum-theme');
  }

  get isResidentSelected(): boolean {
    return this.form.get('residencyStatus')?.value === 'resident';
  }

  get uaePassImage(): string {
    const langDir = this.getCurrentLang() === 'ar' ? 'AR' : 'EN';
    const state = this.uaePassPressed ? 'Pressed' : this.uaePassFocused ? 'Focus' : 'Active';
    return `/images/uae-pass/${langDir}/${state}@3x-2.png`;
  }

  getIconPath(icon: string): string {
    const fileName = icon?.split('/').pop() ?? icon;
    if (this.isPlatinumTheme) {
      return `/icons/platinum/${fileName}`;
    }
    return `/icons/${icon}`;
  }

  ngOnInit(): void {
    this.guestTokenService.ensureGuestToken().subscribe({
      next: () => {
        this.lang = this.getCurrentLang();
        this.getListOfCountry();
        this.openFolder();
      },
      error: () => {
        this.sharedService.toastMessage('ERROR', 'SOMETHING_WENT_WRONG', 'error');
      }
    });

    //for autosave trigger
    this.form
      .get('residencyStatus')!
      .valueChanges.pipe(startWith(this.form.get('residencyStatus')!.value), takeUntil(this.destroy$))
      .subscribe(() => this.updateResidencyValidators());
    this.form
      .get('benefit')!
      .valueChanges.pipe(startWith(this.form.get('benefit')!.value), takeUntil(this.destroy$))
      .subscribe(() => this.updateBenefitValidators());
    combineLatest([
      this.form.get('residencyStatus')!.valueChanges.pipe(
        startWith(this.form.get('residencyStatus')!.value)
      ),
      this.form.get('emiratesId')!.valueChanges.pipe(
        startWith(this.form.get('emiratesId')!.value)
      ),
      this.form.get('passportNumber')!.valueChanges.pipe(
        startWith(this.form.get('passportNumber')!.value)
      )
    ])
      .pipe(
        debounceTime(400),
        distinctUntilChanged(
          ([prevResidency, prevEmiratesId, prevPassport], [currResidency, currEmiratesId, currPassport]) =>
            prevResidency === currResidency &&
            prevEmiratesId === currEmiratesId &&
            prevPassport === currPassport
        ),
        takeUntil(this.destroy$)
      )
      .subscribe(() => this.triggerDraftRegistrationSave());
    //End

    this.search$
      .pipe(
        debounceTime(300),
        distinctUntilChanged(
          (prev, curr) =>
            prev?.searchText === curr?.searchText && prev?.searchBy === curr?.searchBy
        ),
        tap((term) => {
          if (!term?.searchText || term.searchText.trim().length === 0) {
            this.members = [];
          }
        }),
        filter((term) => term?.searchText && term.searchText.trim().length >= 2),
        tap(() => (this.loadingMember = true)),
        switchMap((term) =>
          this.sharedService
            .getDCAllMembers(term.searchText, term.searchBy)
            .pipe(finalize(() => (this.loadingMember = false)))
        ),
        takeUntil(this.destroy$)
      )
      .subscribe((response: any) => {
        const errorCode = response?.['Error Code'];
        const errorMessage = response?.['Error Message'];
        const isSuccessCode =
          errorCode === '' || errorCode == null || errorCode === 0 || errorCode === '0';
        const isSuccessMessage = errorMessage === '' || errorMessage == null;
        if (isSuccessCode && isSuccessMessage && response?.SiebelMessage?.["DC Account Thin"]) {
          const memberData = Utils.checkIsArray(response['SiebelMessage']["DC Account Thin"]);
          const lang = this.getCurrentLang();
          this.members = memberData.map((item: any) => ({
            ...item,
            Name: lang === 'ar'
              ? (item["DC Name Arabic"])
              : (item["DC Name English"]),
            Value: lang === 'ar'
              ? (item["DC Name Arabic"])
              : (item["DC Name English"])
          }));
        } else {
          this.members = [];
        }
      });
  }

  countryList: any[] = [];
  loadingCountry: boolean = false
  getListOfCountry() {
    this.countryList = [];
    this.loadingCountry = true;
    let request = {
      body: {
        ProcessName: "DC Get List Of Values WF",
        Type: LookupKey.COUNTRY,
        "LanguageCode": this.sharedService.getCurrentLang() == "ar" ? 'ARA' : "ENU",
        ParentValue: ''
      }
    }
    this.api
      .post(ApiRoot.DCCICPIntegration, LookupKey.LOOKUP_URL, request)
      .subscribe({
        next: (result: any) => {
          this.loadingCountry = false;
          if (result.SiebelMessage?.['List Of Values'] && result["Error Message"] == null || result["Error Message"] == "") {
            this.countryList = Utils.checkIsArray(result.SiebelMessage['List Of Values']);
          }
        },
        error: () => {
          this.loadingCountry = false;
        }
      })

  }
  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  // This method is used to go back to login
  goBack() {
    this.router.navigate(['/', RoutesPath.PUBLIC.LOGIN]);
  }

  // This method is used to validate role selection
  hasRoleSelected(): boolean {
    if (!this.isBusinessBenefitSelected) {
      return true;
    }
    const roles = this.form.get('roles')?.value;
    return Array.isArray(roles) && roles.length > 0;
  }

  get isBusinessBenefitSelected(): boolean {
    return this.form.get('benefit')?.value === 'business';
  }

  get isPersonalBenefitSelected(): boolean {
    return this.form.get('benefit')?.value === 'personal';
  }

  get isBusinessDubaiBased(): boolean {
    return this.form.get('businessLocation')?.value === 'dubai';
  }

  get visibleDocumentTypes(): AllowedDocumentType[] {
    if (!this.mainFromSectionDocs?.length) {
      return [];
    }

    if (this.isBusinessBenefitSelected) {
      return this.mainFromSectionDocs.filter((doc) => this.isBusinessDocumentType(doc));
    }

    if (this.isPersonalBenefitSelected) {
      const personalDoc = this.getRequiredPersonalDocumentType();
      return personalDoc ? [personalDoc] : [];
    }

    return [];
  }

  get shouldShowDocumentSection(): boolean {
    return this.visibleDocumentTypes.length > 0;
  }

  get roleOptionsForSelection(): any[] {
    const selectedRoles = this.form.get('roles')?.value || [];
    const hasRepresentative = selectedRoles.includes('representative');
    const hasOwnerOrPartnerOrManager =
      selectedRoles.includes('owner') ||
      selectedRoles.includes('partner') ||
      selectedRoles.includes('manager');

    return this.roleOptions.map((option) => {
      if (option.value === 'representative') {
        return { ...option, disabled: hasOwnerOrPartnerOrManager };
      }
      return { ...option, disabled: hasRepresentative };
    });
  }

  onRoleSelectionChange(selectedRoles: string[]): void {
    const sanitizedRoles = this.sanitizeRoleSelection(selectedRoles);
    this.previousRoles = [...sanitizedRoles];

    const hasDifferentRoles =
      sanitizedRoles.length !== selectedRoles.length ||
      sanitizedRoles.some((role, index) => role !== selectedRoles[index]);

    if (hasDifferentRoles) {
      this.form.get('roles')?.setValue(sanitizedRoles);
    }
  }

  handleCompanySearch(searchEvent: any) {
    const normalizedPayload = this.normalizeCompanySearchPayload(searchEvent);
    this.search$.next(normalizedPayload);
  }

  private normalizeCompanySearchPayload(searchEvent: any): { searchBy: string; searchText: string } {
    const fallbackSearchBy = this.companySearchByOptions?.[0]?.value || 'CSN';
    if (typeof searchEvent === 'string') {
      return {
        searchBy: fallbackSearchBy,
        searchText: searchEvent.trim()
      };
    }

    const searchBy = searchEvent?.searchBy || fallbackSearchBy;
    const rawTerm = searchEvent?.searchText ?? searchEvent?.term ?? '';
    const searchText = typeof rawTerm === 'string' ? rawTerm.trim() : String(rawTerm ?? '').trim();
    return { searchBy, searchText };
  }

  private getCurrentLang(): string {
    return (this.translate.currentLang || localStorage.getItem('selectedLang') || 'en').toLowerCase();
  }

  get completionPercent(): number {
    const fields = this.getProgressFields();
    const total = fields.length;
    if (!total) {
      return 0;
    }
    const filled = fields.filter((path) => this.isFieldFilled(path)).length;
    return Math.round((filled / total) * 100);
  }

  get isRegistrationFlowInProgress(): boolean {
    return this.isSendingVerifyOtp || this.isSubmitting || this.verifyEmailModal || !!this.pendingRegistrationData;
  }

  private getProgressFields(): string[] {
    const fields = [
      'residencyStatus',
      'benefit',
      'nameEn',
      'nameAr',
      'nationality',
      'email',
      'phone',
      'username',
      'password',
      'confirmPassword',
      'termsAccepted'
    ];

    if (this.isBusinessBenefitSelected) {
      fields.push('businessLocation', 'issuingAuthority', 'companySearch', 'roles');
    }

    const residencyStatus = this.form.get('residencyStatus')?.value;
    if (residencyStatus === 'resident') {
      fields.push(
        'emiratesId',
        'emiratesDob.day',
        'emiratesDob.month',
        'emiratesDob.year'
      );
    } else if (residencyStatus === 'non-resident') {
      fields.push(
        'passportNumber',
        'passportExpiry.day',
        'passportExpiry.month',
        'passportExpiry.year'
      );
    }

    return fields;
  }

  private isFieldFilled(path: string): boolean {
    const control = this.form.get(path);
    if (!control) {
      return false;
    }
    const value = control.value;
    if (Array.isArray(value)) {
      return value.length > 0;
    }
    if (value === null || value === undefined) {
      return false;
    }
    if (typeof value === 'string') {
      return value.trim().length > 0;
    }
    return true;
  }

  private updateResidencyValidators(): void {
    const residencyStatus = this.form.get('residencyStatus')?.value;
    const isResident = residencyStatus === 'resident';
    const isNonResident = residencyStatus === 'non-resident';

    this.detailNotFound = false;
    this.detailFound = false;
    if (isResident) {
      this.clearIntegrationFields();
      this.setIntegrationFieldsDisabled(true);
    } else {
      this.setIntegrationFieldsDisabled(false);
    }

    this.toggleRequired(isResident, [
      'emiratesId',
      'emiratesDob.day',
      'emiratesDob.month',
      'emiratesDob.year'
    ]);
    this.toggleRequired(isNonResident, [
      'passportNumber',
      'passportExpiry.day',
      'passportExpiry.month',
      'passportExpiry.year'
    ]);
  }

  private updateBenefitValidators(): void {
    const isBusiness = this.isBusinessBenefitSelected;
    this.toggleRequired(isBusiness, ['businessLocation', 'companySearch', 'issuingAuthority', 'roles']);
    // if (this.form.get('benefit')?.value && !this.srFolder && !this.loadingDocumentFolder) {
    //   this.openFolder();
    // }

    if (!isBusiness) {
      this.members = [];
      this.roleTouched = false;
      this.form.get('businessLocation')?.setValue(null, { emitEvent: false });
      this.form.get('companySearch')?.setValue('', { emitEvent: false });
      this.form.get('issuingAuthority')?.setValue(null, { emitEvent: false });
      this.form.get('roles')?.setValue([], { emitEvent: false });
      this.previousRoles = [];
    }
  }

  private openFolder(): void {
    if (this.loadingDocumentFolder) {
      return;
    }

    this.loadingDocumentFolder = true;
    this.sharedService
      .getDocuments(this.srNumber, this.documentServiceType.UM_NU, this.srNumber == "" ? true : false)
      .pipe(finalize(() => (this.loadingDocumentFolder = false)))
      .subscribe({
        next: (response: any) => {
          if (response?.success === false) {
            this.mainFromSectionDocs = [];
            this.sharedService.toastMessage('error', response.message, 'error');
            return;
          }

          this.srFolder = response?.data ? response.data : response;
          this.mainFromSectionDocs = this.srFolder?.allowedDocumentTypes ?? [];
        },
        error: () => {
          this.mainFromSectionDocs = [];
        }
      });
  }

  handelFileEvent(action: any, _documentField: any): void {
    if (action === 'c_sr') {
      //this.openFolder();
    }
  }

  private normalizeDocumentTypeName(value: string | null | undefined): string {
    return (value || '')
      .toString()
      .trim()
      .toLowerCase()
      .replace(/[_-]+/g, ' ')
      .replace(/[^\w\s]/g, ' ')
      .replace(/\s+/g, ' ');
  }

  private isExactDocumentName(doc: AllowedDocumentType | undefined, expectedName: string): boolean {
    return this.normalizeDocumentTypeName(doc?.nameEn) === this.normalizeDocumentTypeName(expectedName);
  }

  private isBusinessDocumentType(doc: AllowedDocumentType): boolean {
    const normalized = this.normalizeDocumentTypeName(doc?.nameEn);
    return this.businessDocumentNames.some(
      (name) => normalized === this.normalizeDocumentTypeName(name)
    );
  }

  private isResidentPersonalDocumentType(doc: AllowedDocumentType): boolean {
    return this.isExactDocumentName(doc, this.residentPersonalDocumentName);
  }

  private isNonResidentPersonalDocumentType(doc: AllowedDocumentType): boolean {
    return this.isExactDocumentName(doc, this.nonResidentPersonalDocumentName);
  }

  private getRequiredPersonalDocumentType(): AllowedDocumentType | undefined {
    const residencyStatus = this.form.get('residencyStatus')?.value;

    if (residencyStatus === 'resident') {
      return this.mainFromSectionDocs.find((doc) => this.isResidentPersonalDocumentType(doc));
    }

    if (residencyStatus === 'non-resident') {
      return this.mainFromSectionDocs.find((doc) => this.isNonResidentPersonalDocumentType(doc));
    }

    return undefined;
  }

  private isDocumentMandatory(doc: AllowedDocumentType): boolean {
    if (this.isBusinessBenefitSelected) {
      return !!doc?.mandatory;
    }

    if (this.isPersonalBenefitSelected) {
      const personalDoc = this.getRequiredPersonalDocumentType();
      return !!personalDoc && personalDoc.nameEn === doc?.nameEn;
    }

    return false;
  }

  private checkRequiredDocuments(): boolean {
    const visibleDocs = this.visibleDocumentTypes;
    if (!visibleDocs.length) {
      return false;
    }

    const fileCmps = this.fileComponents?.toArray?.() ?? [];
    if (fileCmps.length < visibleDocs.length) {
      return false;
    }

    let hasMissing = false;
    for (let index = 0; index < visibleDocs.length; index++) {
      const doc = visibleDocs[index];
      const cmp = fileCmps[index];
      if (!cmp) {
        continue;
      }
      if (typeof cmp.checkMissing === 'function' && cmp.checkMissing(this.isDocumentMandatory(doc))) {
        hasMissing = true;
      }
    }
    return hasMissing;
  }

  private sanitizeRoleSelection(selectedRoles: string[]): string[] {
    const normalizedSelected = Array.isArray(selectedRoles) ? selectedRoles : [];
    if (!normalizedSelected.length) {
      return [];
    }

    const previousRoles = this.previousRoles || [];
    const addedRole = normalizedSelected.find((role) => !previousRoles.includes(role));
    const hasRepresentative = normalizedSelected.includes('representative');
    const nonRepresentativeRoles = normalizedSelected.filter((role) => role !== 'representative');

    if (!hasRepresentative) {
      return normalizedSelected;
    }

    if (addedRole === 'representative') {
      return ['representative'];
    }

    if (nonRepresentativeRoles.length > 0) {
      return nonRepresentativeRoles;
    }

    return ['representative'];
  }

  private toggleRequired(condition: boolean, controls: string[]): void {
    Utils.addRemoveValidation(this.form, condition, controls);
  }

  loginWithUaePass(): void {
    if (this.uaePassSubmitting) {
      return;
    }
    this.uaePassSubmitting = true;
    const redirectUri = environment.uaePassRedirectUri || `${window.location.origin}/${RoutesPath.PUBLIC.REGISTER}`;
    this.http.post<UaePassInitResponse>(this.uaePassInitUrl, { redirect_uri: redirectUri }).subscribe({
      next: (response) => {
        if (response?.flowId) {
          sessionStorage.setItem('uaePassFlowId', response.flowId);
        }
        if (response?.redirectUrl) {
          window.location.href = response.redirectUrl;
          return;
        }
        this.uaePassSubmitting = false;
      },
      error: () => {
        this.uaePassSubmitting = false;
      }
    });
  }

  onUaePassFocus(): void {
    this.uaePassFocused = true;
  }

  onUaePassBlur(): void {
    this.uaePassFocused = false;
    this.uaePassPressed = false;
  }

  onUaePassPress(isPressed: boolean): void {
    this.uaePassPressed = isPressed;
  }

  onUaePassKey(isPressed: boolean, event: KeyboardEvent): void {
    if (event.key === ' ' || event.key === 'Enter') {
      this.uaePassPressed = isPressed;
    }
  }

  // This method is used to get detail by emirate id
  getDetailByEmirateId(): void {
    if (this.form.get('emiratesId')?.invalid) {
      return;
    }
    this.detailNotFound = false;
    this.detailFound = false;
    this.loadingEmirateInfo = false;

    const emirateId = this.form.get('emiratesId')?.value;
    if (emirateId) {
      this.loadingEmirateInfo = true;
      this.getPersonDetail(emirateId);
    }
  }

  // This method is used to get person detail using emirate id
  private getPersonDetail(emirateId: string): void {
    const requestPayload = {
      identityNumber: emirateId
    };

    const fetchPersonDetail = () => {
      this.api.post(
        ApiRoot.ICA_PERSON_INQUIRY,
        ApiEndPoints.GET_PERSON_INQUIRY,
        requestPayload
      ).subscribe({
        next: (response: any) => {
          if (response && response.errorCode == null) {
            this.detailFound = true;
            this.loadingEmirateInfo = false;
            if (response.personProfileType) {
              const profile = response.personProfileType;
              const birthDate = profile.birthDate;
              const date = new Date(birthDate);
              date.setUTCDate(date.getUTCDate() + 1);
              const day = String(date.getUTCDate()).padStart(2, '0');
              const month = String(date.getUTCMonth() + 1).padStart(2, '0');
              const year = date.getUTCFullYear();
              const fullDate = { day: day, month: month, year: year };
              const emailAddress = profile.addresses.address.localAddress.emailAddress;
              const mobileNo = profile.addresses.address.localAddress.mobileNo;
              const userNationality = profile.nationality?.descriptionEnglish;
              const nationality = userNationality ? Utils.toCapitalize(userNationality) : null;
              const fullNameArabic = profile.personName?.fullNameArabic
                ?? response?.FullNameAr
                ?? response?.fullNameAr
                ?? response?.fullNameArabic
                ?? '';
              const fullNameEnglish = profile.personName?.fullNameEnglish
                ?? response?.FullNameEn
                ?? response?.fullNameEn
                ?? response?.fullNameEnglish
                ?? '';

              this.form.controls['emiratesDob']?.setValue(fullDate);
              this.form.get('emiratesDob')?.updateValueAndValidity();

              this.form.get('nameEn')?.setValue(fullNameEnglish);
              this.form.get('nameAr')?.setValue(fullNameArabic);
              this.form.get('nationality')?.setValue(nationality);
              this.form.get('email')?.setValue(emailAddress);
              this.form.get('phone')?.setValue(mobileNo);
              this.setIntegrationFieldsDisabled(true);
            } else {
              this.detailFound = false;
              this.detailNotFound = true;
              this.loadingEmirateInfo = false;
              this.form.get('nameEn')?.setValue('');
              this.form.get('nameAr')?.setValue('');
              this.form.get('nationality')?.setValue(null);
              this.form.get('email')?.setValue('');
              this.form.get('phone')?.setValue('');
              this.setIntegrationFieldsDisabled(true);
            }
          } else {
            this.detailFound = false;
            this.detailNotFound = true;
            this.loadingEmirateInfo = false;
            this.setIntegrationFieldsDisabled(true);
          }
        },
        error: () => {
          this.detailFound = false;
          this.detailNotFound = true;
          this.loadingEmirateInfo = false;
          this.setIntegrationFieldsDisabled(true);
        }
      });
    };

    if (this.sharedService.getUser()) {
      fetchPersonDetail();
      return;
    }

    this.guestTokenService.ensureGuestToken().subscribe({
      next: () => {
        fetchPersonDetail();
      },
      error: () => {
        this.detailFound = false;
        this.detailNotFound = true;
        this.loadingEmirateInfo = false;
        this.sharedService.toastMessage('ERROR', 'SOMETHING_WENT_WRONG', 'error');
      }
    });
  }

  private setIntegrationFieldsDisabled(disabled: boolean): void {
    ['nameEn', 'nameAr', 'nationality'].forEach((controlName) => {
      const control = this.form.get(controlName);
      if (!control) {
        return;
      }
      if (disabled) {
        control.disable({ emitEvent: false });
      } else {
        control.enable({ emitEvent: false });
      }
    });
  }

  private clearIntegrationFields(): void {
    ['nameEn', 'nameAr', 'nationality'].forEach((controlName) => {
      const control = this.form.get(controlName);
      if (control) {
        control.setValue(null, { emitEvent: false });
      }
    });
  }

  // This method is used to submit the registration request
  submit() {
    this.registerError = '';
    this.roleTouched = true;

    const isResident = this.form.get('residencyStatus')?.value === 'resident';
    if (isResident) {
      const emiratesDob = this.form.get('emiratesDob')?.value;
      if (!this.form.get('emiratesId')?.value || !emiratesDob?.day) {
        this.detailNotFound = true;
        return;
      }
    }
    if (this.form.invalid || !this.hasRoleSelected()) {
      this.form.markAllAsTouched();
      return;
    }

    if (this.checkRequiredDocuments()) {
      this.sharedService.toastMessage('ERROR', "FILL_REQUIRED_FIELDS_DOCUMENTS", 'error', false);
      return;
    }

    const termsAcceptedControl = this.form.get('termsAccepted');
    termsAcceptedControl?.markAsTouched();
    if (!termsAcceptedControl?.value) {
      this.form.get('termsAccepted')?.setValue('');
      termsAcceptedControl?.setErrors({ required: true });
      return;
    }

    if (this.verifyEmailModal) {
      return;
    }

    const formValue = this.form.getRawValue();
    const userName = (formValue.username || '').trim();
    const password = formValue.password || '';

    // Hold submission until email verification is completed.
    this.userLoginName = userName.toLowerCase();
    this.userEmail = (formValue.email || '').trim().toLowerCase();
    this.pendingRegistrationData = {
      formValue,
      userName,
      password
    };
    this.sendOtpBeforeOpenVerifyModal();
    // this.onVerifyEmailSuccess();
  }

  onVerifyEmailSuccess(): void {
    this.verifyEmailModal = false;
    if (!this.pendingRegistrationData || this.isSubmitting) {
      return;
    }

    const { formValue, userName, password } = this.pendingRegistrationData;
    this.pendingRegistrationData = null;
    this.proceedWithRegistration(formValue, userName, password);
  }

  onVerifyEmailClosed(): void {
    // User closed modal or stopped verification: abort submission flow.
    this.verifyEmailModal = false;
    this.pendingRegistrationData = null;
  }

  private sendOtpBeforeOpenVerifyModal(): void {
    if (this.isSendingVerifyOtp || !this.userLoginName || !this.userEmail) {
      return;
    }

    const requestPayload = {
      body: {
        ProcessName: 'DC CP Email Verification Process WF',
        LoginName: this.userLoginName,
        EmailAddr: this.userEmail,
        ReGenOTPFlg: ''
      }
    };

    this.isSendingVerifyOtp = true;
    this.api
      .post(ApiRoot.DCCICPIntegration, ApiEndPoints.GENERATE_EMAIL_OTP, requestPayload)
      .subscribe({
        next: (response: any) => {
          this.isSendingVerifyOtp = false;
          if (this.isVerifyOtpResponseOk(response)) {
            this.verifyEmailModal = true;
          } else {
            this.pendingRegistrationData = null;
            this.sharedService.toastMessage(
              'ERROR',
              response?.['Error Message'] || 'TECHNICAL_ERROR',
              'error'
            );
          }
        },
        error: () => {
          this.isSendingVerifyOtp = false;
          this.pendingRegistrationData = null;
          this.sharedService.toastMessage('ERROR', 'TECHNICAL_ERROR', 'error');
        }
      });
  }
  private isVerifyOtpResponseOk(response: any): boolean {
    const errorCode = response?.['Error Code'];
    const errorMsg = response?.['Error Message'];
    const codeOk = errorCode === 0 || errorCode === '0' || errorCode === '' || errorCode == null;
    const msgOk = errorMsg === '' || errorMsg == null;
    return codeOk && msgOk;
  }

  private proceedWithRegistration(formValue: any, userName: string, password: string): void {
    const fullName = (formValue.nameEn || '').trim();
    const nameParts = fullName ? fullName.split(/\s+/) : [];
    const givenName = nameParts.length ? nameParts[0] : '';
    const lastName = nameParts.length > 1 ? nameParts.slice(1).join(' ') : '';
    const isResident = formValue.residencyStatus === 'resident';
    const isNonResident = formValue.residencyStatus === 'non-resident';
    const emiratesId = isResident ? (formValue.emiratesId || '') : '';
    const passport = isNonResident ? (formValue.passportNumber || '') : '';

    const phone = formValue.phone && formValue.countryCode ? `${formValue.countryCode}${formValue.phone}` : "";

    const payload = {
      user: {
        username: userName,
        realm: 'primary',
        password,
        claims: [
          {
            uri: 'http://wso2.org/claims/emailaddress',
            value: formValue.email || ''
          },
          {
            uri: 'http://wso2.org/claims/givenname',
            value: fullName //givenName
          },
          {
            uri: 'http://wso2.org/claims/lastname',
            value: (formValue.nameAr || '').trim() //lastName
          },
          {
            uri: 'http://wso2.org/claims/country',
            value: formValue.nationality || ''
          },
          {
            uri: 'http://wso2.org/claims/mobile',
            value: phone || ''
          },
          {
            uri: 'http://wso2.org/claims/title',
            value: ''
          },
          {
            uri: 'http://wso2.org/claims/emiratesId',
            value: emiratesId
          },
          {
            uri: 'http://wso2.org/claims/passport',
            value: passport
          }
        ]
      }
    };

    this.isSubmitting = true;
    this.http.post(this.buildWso2Url('api/identity/user/v1.0/me'), payload).subscribe({
      next: () => {
        this.loginAndStoreTokens(userName, password, formValue);
        // this.updateRegistrationDetails(formValue, userName, password);
      },
      error: (error) => {
        const description = error?.error?.description || '';
        this.registerError = description || 'REGISTER.ERROR_GENERIC';
        this.isSubmitting = false;
      }
    });
  }

  private buildWso2Url(path: string): string {
    const base = environment.wso2APIURL.replace(/\/+$/, '');
    const cleanPath = path.replace(/^\/+/, '');
    return `${base}/${cleanPath}`;
  }

  private loginAndStoreTokens(username: string, password: string, formValue: any): void {
    this.http.post<any>(this.loginUrl, { username, password }).subscribe({
      next: (response) => {
        const expiresIn = Number(response?.expires_in || 0);
        const expiresAt = expiresIn ? Date.now() + expiresIn * 1000 : null;
        const userData = {
          user: { username },
          access_token: response.access_token,
          refresh_token: response.refresh_token,
          id_token: response.id_token,
          token: response.id_token || response.access_token,
          token_type: response.token_type,
          scope: response.scope,
          expiresAt
        };
        localStorage.setItem('login_user', JSON.stringify(userData));
        this.updateRegistrationDetails(formValue, username, password, SrActionType.SUBMIT);
        this.router.navigate([RoutesPath.DASHBOARD]);
      },
      error: () => {
        this.isSubmitting = false;
      },
      complete: () => {
        this.isSubmitting = false;
      }
    });
  }

  getAccountIdFromCompanySearch(): any {
    const companyValue = this.form.get('companySearch')?.value;
    if (this.members && this.members.length > 0 && companyValue) {
      const normalizedCompanyValue = String(companyValue).toLowerCase().trim();
      const matchedMember: any = this.members.find(member => {
        const comparableValues = [
          member?.Value,
          member?.Name,
          member?.['Account Number'],
          member?.['CSN']
        ];
        return comparableValues.some((value) =>
          String(value ?? '').toLowerCase().trim() === normalizedCompanyValue
        );
      });
      return matchedMember ? matchedMember?.['Account Id'] || matchedMember?.['CSN'] : '';
    } else {
      return '';
    }
  }

  private formatDateGroup(value: any): string {
    if (!value?.day || !value?.month || !value?.year) {
      return '';
    }
    const day = String(value.day).padStart(2, '0');
    const month = String(value.month).padStart(2, '0');
    const year = String(value.year);
    return `${day}/${month}/${year}`;
  }

  private triggerDraftRegistrationSave(): void {
    const formValue = this.form.getRawValue();
    const residencyStatus = formValue.residencyStatus;
    const emiratesId = (formValue.emiratesId || '').toString().trim();
    const passportNumber = (formValue.passportNumber || '').toString().trim();

    const hasResidentIdentity = residencyStatus === 'resident' && emiratesId;
    const hasNonResidentIdentity = residencyStatus === 'non-resident' && passportNumber;

    if (!hasResidentIdentity && !hasNonResidentIdentity) {
      this.lastDraftIdentityKey = '';
      return;
    }

    const draftIdentityKey = residencyStatus === 'resident'
      ? `resident:${emiratesId}`
      : `non-resident:${passportNumber}`;

    if (this.lastDraftIdentityKey === draftIdentityKey) {
      return;
    }

    this.lastDraftIdentityKey = draftIdentityKey;
    const username = (formValue.username || '').trim();
    this.updateRegistrationDetails(formValue, username, '', SrActionType.DRAFT);
  }

  private updateRegistrationDetails(formValue: any, username: string, password: string, method: string): void {
    const roles = Array.isArray(formValue.roles) ? formValue.roles : [];
    const companyValue = formValue.companySearch;
    const accountId = this.getAccountIdFromCompanySearch();
    const companyName = typeof companyValue === 'string'
      ? companyValue
      : companyValue?.name || companyValue?.label || companyValue?.companyName || '';

    const payload = new NewUserRequest();
    payload.body["Login Name"] = method == SrActionType.SUBMIT ? username : '';
    payload.body["DC Method"] = (method == SrActionType.SUBMIT ? 'SUBMIT' : 'SAVEASDRAFT');
    payload.body["DC Channel"] = this.lang == 'ar' ? FieldValue.CHANEL_ONLINE_AR : FieldValue.CHANEL_ONLINE_EN;
    payload.body["DC Payment Method"] = this.lang == 'ar' ? FieldValue.PAYMENT_METHOD_ONLINE_AR : FieldValue.PAYMENT_METHOD_ONLINE_EN;
    payload.body["SR Type"] = ServiceName.USER_MANAGEMENT;
    payload.body["SR Sub Type"] = ServiceName.New_User;
    payload.body["Account Id"] = accountId;
    payload.body["Member Name English"] = formValue.nameEn || '';
    payload.body["Member Name Arabic"] = formValue.nameAr || '';
    payload.body["DC Residence of UAE"] = formValue.residencyStatus === 'resident' ? 'Yes' : 'No';
    payload.body["Emirates Id"] = formValue.residencyStatus === 'resident' ? (formValue.emiratesId || '') : '';
    payload.body["Date of Birth"] = formValue.residencyStatus === 'resident'
      ? this.formatDateGroup(formValue.emiratesDob)
      : '';
    payload.body["DC Passport Number"] = formValue.residencyStatus === 'non-resident'
      ? (formValue.passportNumber || '')
      : '';
    payload.body["ID Expiration Date"] = formValue.residencyStatus === 'non-resident'
      ? this.formatDateGroup(formValue.passportExpiry)
      : '';
    payload.body["DC Nationality"] = formValue.nationality || '';
    payload.body["Email Address"] = formValue.email || '';
    payload.body["Home Phone #"] = formValue.phone || '';
    payload.body["DC License Issuing Authority"] = formValue.issuingAuthority || '';
    payload.body["SR Number"] = this.srNumber || '';
    payload.body["Language"] = this.sharedService.getCurrentLang() === 'ar' ? 'ARA' : 'ENU';

    const updateDetails =
      payload.body["SiebelMessage"]["ListOfDCUpdateUserRegIO"]["Update User Registration Details"];
    updateDetails["Account Id"] = accountId;
    updateDetails["DC Business Expected Benefits"] = formValue.benefit === 'business' ? 'Y' : 'N';
    updateDetails["DC Dubai Based"] = formValue.businessLocation === 'dubai' ? 'Y' : 'N';
    updateDetails["DC Registration Company Name"] = companyName || '';
    updateDetails["DC Registration Manager Role"] = roles.includes('manager') ? 'Y' : 'N';
    updateDetails["DC Registration Owner Role"] = roles.includes('owner') ? 'Y' : 'N';
    updateDetails["DC Registration Rep Role"] = roles.includes('representative') ? 'Y' : 'N';
    updateDetails["Id"] = '';

    this.api.post(ApiRoot.DCCICPIntegration, ApiEndPoints.RUN_PROCESS, payload).subscribe({
      next: (response: any) => {
        if (response && response['Error Code'] == "") {
          this.srNumber = response['SR Number'];
          sessionStorage.setItem('newUserSR', response['SR Number']);
          this.openFolder();
        }
        //this.loginAndStoreTokens(username, password);
      },
      error: () => {
        // this.loginAndStoreTokens(username, password);
      }
    });
  }

  // This method is used to remind later
  remindLater() {
    this.router.navigate(['/', RoutesPath.PUBLIC.LOGIN]);
  }

  openMembershipDisclaimer(event?: Event): void {
    event?.preventDefault();
    this.showMembershipDisclaimerModal = true;
  }

  onMembershipDisclaimerSubmit(isAccepted: boolean): void {
    this.showMembershipDisclaimerModal = false;
    // if (isAccepted) {
    this.form.get('termsAccepted')?.setValue(true);
    this.form.get('termsAccepted')?.markAsTouched();
    // }
  }
}
